import React, { Fragment, useState } from 'react';
import { useRecordContext } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { capitalize } from 'inflection';
import LinkField from 'components/LinkField';
import { formatDateTime } from 'components/DateTimeField';
import { DisplayHelpers } from 'utils/helpers';
import reverse from 'lodash/reverse';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import isNil from 'lodash/isNil';

function getBlendedRatesDiff(originalRecord, updatedRecord, isUpdate) {
  const originalRateSettingsDisplayHelper =
    originalRecord &&
    new RateSettingsDisplayHelper({
      settings: originalRecord,
    });
  const originalTen99BlendedRateMarkup =
    originalRateSettingsDisplayHelper?.getBlendedRateMarkup('ten99');
  const originalW2BlendedRateMarkup =
    originalRateSettingsDisplayHelper?.getBlendedRateMarkup('w2.local');
  const originalTen99UseBlendedRate = originalRecord?.ten99?.use_blended_rate;
  const originalW2UseBlendedRate = originalRecord?.w2?.local?.use_blended_rate;

  const updatedRateSettingsDisplayHelper =
    updatedRecord &&
    new RateSettingsDisplayHelper({
      settings: updatedRecord,
    });
  const updatedTen99BlendedRateMarkup =
    updatedRateSettingsDisplayHelper?.getBlendedRateMarkup('ten99');
  const updatedW2BlendedRateMarkup =
    updatedRateSettingsDisplayHelper?.getBlendedRateMarkup('w2.local');
  const updatedTen99UseBlendedRate = updatedRecord?.ten99?.use_blended_rate;
  const updatedW2UseBlendedRate = updatedRecord?.w2?.local?.use_blended_rate;

  const hasUpdatedTen99BlendedRateMarkup =
    !isUpdate || `${updatedTen99BlendedRateMarkup}` !== `${originalTen99BlendedRateMarkup}`;
  const hasUpdatedW2BlendedRateMarkup =
    !isUpdate || `${updatedW2BlendedRateMarkup}` !== `${originalW2BlendedRateMarkup}`;
  const hasUpdatedTen99UseBlendedRate =
    !isUpdate || `${updatedTen99UseBlendedRate}` !== `${originalTen99UseBlendedRate}`;
  const hasUpdatedW2UseBlendedRate =
    !isUpdate || `${updatedW2UseBlendedRate}` !== `${originalW2UseBlendedRate}`;

  return {
    originalTen99BlendedRateMarkup,
    originalW2BlendedRateMarkup,
    originalTen99UseBlendedRate,
    originalW2UseBlendedRate,
    updatedTen99BlendedRateMarkup,
    updatedW2BlendedRateMarkup,
    updatedTen99UseBlendedRate,
    updatedW2UseBlendedRate,
    hasUpdatedTen99BlendedRateMarkup,
    hasUpdatedW2BlendedRateMarkup,
    hasUpdatedTen99UseBlendedRate,
    hasUpdatedW2UseBlendedRate,
  };
}

const RateAudits = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  return !!record.audits ? (
    <>
      <Button variant="outlined" color="primary" onClick={() => setOpen(true)}>
        View Rates Change Log
      </Button>

      <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Rates Change Log</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Original</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reverse([...record.audits]).map(
                ({ action, account, audited_changes, created_at, id, user_id }) => {
                  const isUpdate = action === 'update';
                  const [originalRecord, updatedRecord] = isUpdate
                    ? audited_changes.settings
                    : [audited_changes.settings];

                  const hasUpdatedChargeCents =
                    !isUpdate ||
                    `${updatedRecord?.ten99?.charge_rate_cents}` !==
                      `${originalRecord?.ten99?.charge_rate_cents}`;
                  const hasUpdatedMarginPercent =
                    !isUpdate ||
                    `${updatedRecord?.ten99?.margin_percent_1099}` !==
                      `${originalRecord?.ten99?.margin_percent_1099}`;

                  const {
                    originalTen99BlendedRateMarkup,
                    originalW2BlendedRateMarkup,
                    originalTen99UseBlendedRate,
                    originalW2UseBlendedRate,
                    updatedTen99BlendedRateMarkup,
                    updatedW2BlendedRateMarkup,
                    updatedTen99UseBlendedRate,
                    updatedW2UseBlendedRate,
                    hasUpdatedTen99BlendedRateMarkup,
                    hasUpdatedW2BlendedRateMarkup,
                    hasUpdatedTen99UseBlendedRate,
                    hasUpdatedW2UseBlendedRate,
                  } = getBlendedRatesDiff(originalRecord, updatedRecord, isUpdate);

                  return (
                    <Fragment key={`audit-table-row-${id}`}>
                      {hasUpdatedChargeCents && (
                        <TableRow>
                          <TableCell>{capitalize(action)}</TableCell>
                          <TableCell>
                            {formatDateTime({ timeZone: 'America/Los_Angeles' })(
                              'MM/dd/yyyy HH:mm zzz',
                            )(created_at)}
                          </TableCell>
                          <TableCell>
                            {!!account && (
                              <LinkField to={`/Account/${user_id}/show`} href="_blank">
                                {account.name}
                              </LinkField>
                            )}
                          </TableCell>
                          <TableCell>charge_rate_cents</TableCell>
                          <TableCell title={originalRecord?.ten99?.charge_rate_cents}>
                            {originalRecord?.ten99?.charge_rate_cents &&
                              DisplayHelpers.money(originalRecord?.ten99?.charge_rate_cents)}
                          </TableCell>
                          <TableCell title={updatedRecord?.ten99?.charge_rate_cents}>
                            {updatedRecord?.ten99?.charge_rate_cents &&
                              DisplayHelpers.money(updatedRecord?.ten99?.charge_rate_cents)}
                          </TableCell>
                        </TableRow>
                      )}
                      {hasUpdatedMarginPercent && (
                        <TableRow>
                          <TableCell>{capitalize(action)}</TableCell>
                          <TableCell>
                            {formatDateTime({ timeZone: 'America/Los_Angeles' })(
                              'MM/dd/yyyy HH:mm zzz',
                            )(created_at)}
                          </TableCell>
                          <TableCell>
                            {!!account && (
                              <LinkField to={`/Account/${user_id}/show`} href="_blank">
                                {account.name}
                              </LinkField>
                            )}
                          </TableCell>
                          <TableCell>margin_percent_1099</TableCell>
                          <TableCell title={originalRecord?.ten99?.margin_percent_1099}>
                            {originalRecord?.ten99?.margin_percent_1099 &&
                              DisplayHelpers.percentage(originalRecord.ten99?.margin_percent_1099)}
                          </TableCell>
                          <TableCell title={updatedRecord?.ten99?.margin_percent_1099}>
                            {updatedRecord?.ten99?.margin_percent_1099 &&
                              DisplayHelpers.percentage(updatedRecord?.ten99?.margin_percent_1099)}
                          </TableCell>
                        </TableRow>
                      )}
                      {hasUpdatedTen99BlendedRateMarkup && (
                        <TableRow>
                          <TableCell>{capitalize(action)}</TableCell>
                          <TableCell>
                            {formatDateTime({ timeZone: 'America/Los_Angeles' })(
                              'MM/dd/yyyy HH:mm zzz',
                            )(created_at)}
                          </TableCell>
                          <TableCell>
                            {!!account && (
                              <LinkField to={`/Account/${user_id}/show`} href="_blank">
                                {account.name}
                              </LinkField>
                            )}
                          </TableCell>
                          <TableCell>ten99.blended_rate_markup</TableCell>
                          <TableCell
                            title={originalRecord?.ten99?.blended_rates?.blended_rate_markup}
                          >
                            {originalTen99BlendedRateMarkup &&
                              DisplayHelpers.percentage(originalTen99BlendedRateMarkup)}
                          </TableCell>
                          <TableCell
                            title={updatedRecord?.ten99?.blended_rates?.blended_rate_markup}
                          >
                            {updatedTen99BlendedRateMarkup &&
                              DisplayHelpers.percentage(updatedTen99BlendedRateMarkup)}
                          </TableCell>
                        </TableRow>
                      )}
                      {hasUpdatedW2BlendedRateMarkup && (
                        <TableRow>
                          <TableCell>{capitalize(action)}</TableCell>
                          <TableCell>
                            {formatDateTime({ timeZone: 'America/Los_Angeles' })(
                              'MM/dd/yyyy HH:mm zzz',
                            )(created_at)}
                          </TableCell>
                          <TableCell>
                            {!!account && (
                              <LinkField to={`/Account/${user_id}/show`} href="_blank">
                                {account.name}
                              </LinkField>
                            )}
                          </TableCell>
                          <TableCell>w2_per_diem.blended_rate_markup</TableCell>
                          <TableCell
                            title={originalRecord?.w2?.local?.blended_rates?.blended_rate_markup}
                          >
                            {originalW2BlendedRateMarkup &&
                              DisplayHelpers.percentage(originalW2BlendedRateMarkup)}
                          </TableCell>
                          <TableCell
                            title={updatedRecord?.w2?.local?.blended_rates?.blended_rate_markup}
                          >
                            {updatedW2BlendedRateMarkup &&
                              DisplayHelpers.percentage(updatedW2BlendedRateMarkup)}
                          </TableCell>
                        </TableRow>
                      )}
                      {hasUpdatedTen99UseBlendedRate && (
                        <TableRow>
                          <TableCell>{capitalize(action)}</TableCell>
                          <TableCell>
                            {formatDateTime({ timeZone: 'America/Los_Angeles' })(
                              'MM/dd/yyyy HH:mm zzz',
                            )(created_at)}
                          </TableCell>
                          <TableCell>
                            {!!account && (
                              <LinkField to={`/Account/${user_id}/show`} href="_blank">
                                {account.name}
                              </LinkField>
                            )}
                          </TableCell>
                          <TableCell>ten99.use_blended_rate</TableCell>
                          <TableCell title={originalTen99UseBlendedRate}>
                            {isNil(originalTen99UseBlendedRate)
                              ? null
                              : String(originalTen99UseBlendedRate)}
                          </TableCell>
                          <TableCell title={updatedTen99UseBlendedRate}>
                            {isNil(updatedTen99UseBlendedRate)
                              ? null
                              : String(updatedTen99UseBlendedRate)}
                          </TableCell>
                        </TableRow>
                      )}
                      {hasUpdatedW2UseBlendedRate && (
                        <TableRow>
                          <TableCell>{capitalize(action)}</TableCell>
                          <TableCell>
                            {formatDateTime({ timeZone: 'America/Los_Angeles' })(
                              'MM/dd/yyyy HH:mm zzz',
                            )(created_at)}
                          </TableCell>
                          <TableCell>
                            {!!account && (
                              <LinkField to={`/Account/${user_id}/show`} href="_blank">
                                {account.name}
                              </LinkField>
                            )}
                          </TableCell>
                          <TableCell>w2.local.use_blended_rate</TableCell>
                          <TableCell title={originalW2UseBlendedRate}>
                            {isNil(originalW2UseBlendedRate)
                              ? null
                              : String(originalW2UseBlendedRate)}
                          </TableCell>
                          <TableCell title={updatedW2UseBlendedRate}>
                            {isNil(updatedW2UseBlendedRate)
                              ? null
                              : String(updatedW2UseBlendedRate)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                },
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};

export default RateAudits;
